'use client';

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { AuthContextType, User, AuthResponse, UserRole, Organization, UpdateUserData } from '@/types/auth';
import { getSharedApiService, createAuthService, setUnauthorizedCallback } from '@/services';
import { env } from '@/config/environment';
import { useRouter } from 'next/navigation';
import { normalizeRole } from '@/utils/roleUtils';
import { DEFAULT_FEATURES } from '@/config/features';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const TAB_COUNTER_KEY = 'auth_tab_counter';
const TAB_REGISTERED_KEY = 'auth_tab_registered'; // sessionStorage key to track if this tab has been counted

/**
 * Auth Provider Component
 * Manages authentication state and provides auth methods
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const router = useRouter();

  // Create auth service instance
  const apiService = getSharedApiService();
  const authService = createAuthService(apiService);

  // Handle unauthorized access (token expired or invalid)
  const handleUnauthorized = useCallback(() => {
    // Clear user state and stored data
    setUser(null);
    setOrganization(null);
    localStorage.removeItem(env.JWT_STORAGE_KEY);
    localStorage.removeItem(env.USER_STORAGE_KEY);
    localStorage.removeItem(env.ORGANIZATION_STORAGE_KEY);
    router.push('/login');
  }, [router]);

  // Set unauthorized callback for API service
  useEffect(() => {
    setUnauthorizedCallback(handleUnauthorized);
  }, [handleUnauthorized]);

  // Listen for logout events from other tabs
  // Storage events only fire in OTHER tabs, not the current tab
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleStorageChange = (e: StorageEvent) => {
      // Check if logout event was triggered from another tab
      if (e.key === env.LOGOUT_EVENT_KEY && e.newValue) {
        try {
          // Storage events only fire in other tabs, so this is definitely from another tab
          // Clear user state and redirect to login
          setUser(null);
          // Clear auth data (but don't trigger another logout event to avoid loops)
          if (typeof window !== 'undefined') {
            localStorage.removeItem(env.JWT_STORAGE_KEY);
            localStorage.removeItem(env.USER_STORAGE_KEY);
          }
          authService.clearToken();
          router.push('/login');
        } catch (error) {
          console.error('Error processing logout event:', error);
        }
      }
      
      // Also check if token or user data was removed directly (logout from another tab)
      if (e.key === env.JWT_STORAGE_KEY && e.newValue === null && user) {
        setUser(null);
        setOrganization(null);
        router.push('/login');
      }
      
      if (e.key === env.USER_STORAGE_KEY && e.newValue === null && user) {
        setUser(null);
        setOrganization(null);
        router.push('/login');
      }
      
      if (e.key === env.ORGANIZATION_STORAGE_KEY && e.newValue === null && organization) {
        setOrganization(null);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [user, router, authService]);

  // Tab counter mechanism to detect when last tab is closed
  // localStorage is domain-specific, so counter only tracks tabs from this domain
  // Uses sessionStorage to ensure each tab only increments the counter once
  useEffect(() => {
    if (typeof window === 'undefined' || !user) return;

    // Check if this tab has already been registered (using sessionStorage - tab-specific)
    const isTabRegistered = sessionStorage.getItem(TAB_REGISTERED_KEY) === 'true';

    // Validate and reset counter if it seems invalid (too high or negative)
    const validateCounter = () => {
      try {
        const currentCount = parseInt(localStorage.getItem(TAB_COUNTER_KEY) || '0', 10);
        // If counter is unreasonably high (> 5) or negative, reset it
        // Most users won't have more than 5 tabs open for the same app, so > 5 likely indicates accumulation
        // This handles cases where tabs crashed without decrementing
        if (currentCount > 5 || currentCount < 0) {
          console.warn('Tab counter seems invalid, resetting:', currentCount);
          localStorage.setItem(TAB_COUNTER_KEY, '0');
          return 0;
        }
        return currentCount;
      } catch (error) {
        console.error('Error validating counter:', error);
        return 0;
      }
    };

    // Increment tab counter only if this tab hasn't been registered yet
    const incrementTabCounter = () => {
      try {
        // First validate the counter
        const currentCount = validateCounter();
        
        // If tab is registered but counter is 0, it means it was a refresh
        // Re-increment the counter
        if (isTabRegistered && currentCount === 0) {
          localStorage.setItem(TAB_COUNTER_KEY, '1');
          return;
        }
        
        // If tab is already registered and counter is not 0, don't increment again
        if (isTabRegistered) {
          return;
        }

        // Increment counter for new tab
        localStorage.setItem(TAB_COUNTER_KEY, String(currentCount + 1));
        // Mark this tab as registered in sessionStorage (tab-specific, persists across refresh)
        sessionStorage.setItem(TAB_REGISTERED_KEY, 'true');
      } catch (error) {
        console.error('Error incrementing tab counter:', error);
      }
    };

    // Decrement tab counter when tab closes
    const decrementTabCounter = () => {
      // Only decrement if this tab was registered
      if (!isTabRegistered) {
        return;
      }

      try {
        const currentCount = parseInt(localStorage.getItem(TAB_COUNTER_KEY) || '0', 10);
        const newCount = Math.max(0, currentCount - 1);
        
        if (newCount === 0) {
          // Last tab is closing - clear counter
          // Don't clear localStorage here - we'll check on next page load
          // If it's a refresh, sessionStorage will still have the flag
          // If it's a real close, sessionStorage will be cleared
          localStorage.removeItem(TAB_COUNTER_KEY);
        } else {
          localStorage.setItem(TAB_COUNTER_KEY, String(newCount));
        }
      } catch (error) {
        console.error('Error decrementing tab counter:', error);
      }
    };

    // Always validate counter, then increment if needed
    // incrementTabCounter will check if it should increment based on registration status
    validateCounter();
    incrementTabCounter();

    // Flag to prevent double-decrementing (both beforeunload and pagehide can fire)
    let hasDecremented = false;

    // Handle tab close events
    const handleTabClose = () => {
      if (!hasDecremented && isTabRegistered) {
        hasDecremented = true;
        decrementTabCounter();
      }
    };

    // Use pagehide as primary (more reliable) and beforeunload as backup
    // pagehide fires in more cases (including mobile, background tabs, etc.)
    window.addEventListener('pagehide', handleTabClose);
    window.addEventListener('beforeunload', handleTabClose);

    // Cleanup on unmount
    return () => {
      window.removeEventListener('pagehide', handleTabClose);
      window.removeEventListener('beforeunload', handleTabClose);
    };
  }, [user, authService]);

  // Initialize auth state from stored user data on mount
  useEffect(() => {
    const initializeAuth = async () => {
      setIsLoading(true);
      
      try {
        const storedToken = authService.getStoredToken();
        const storedUserData = localStorage.getItem(env.USER_STORAGE_KEY);
        
        // Check if last tab was closed (tab counter is 0 or missing)
        // localStorage is domain-specific, so this only checks tabs from this domain
        // Also validate counter - if it's unreasonably high, reset it
        let tabCount = parseInt(localStorage.getItem(TAB_COUNTER_KEY) || '0', 10);
        if (tabCount > 5 || tabCount < 0) {
          // Counter seems invalid, reset it
          // Most users won't have more than 5 tabs open for the same app, so > 5 likely indicates accumulation
          console.warn('Tab counter invalid on init, resetting:', tabCount);
          localStorage.setItem(TAB_COUNTER_KEY, '0');
          tabCount = 0;
        }
        
        // Check if counter is 0 - this means last tab was closed
        // But we need to distinguish between refresh and actual close
        // sessionStorage persists across refresh, so if it has the flag, it's a refresh
        const isTabRegistered = sessionStorage.getItem(TAB_REGISTERED_KEY) === 'true';
        
        if (tabCount === 0) {
          if (isTabRegistered) {
            // Counter is 0 but sessionStorage has flag - this is a refresh
            // Don't logout, just continue with initialization
            // The tab counter effect will re-increment the counter
          } else {
            // Counter is 0 and no sessionStorage flag - all tabs were closed
            // Clear auth data and logout
            await authService.logout();
            setUser(null);
            setOrganization(null);
            setIsLoading(false);
            return;
          }
        }
        
        if (storedToken && storedUserData) {
          try {
            // First, load user data from localStorage for fast initialization
            const cachedUserDataRaw: any = JSON.parse(storedUserData);
            // Normalize role if needed
            const normalizedRole = normalizeRole(cachedUserDataRaw.role);
            const cachedUserData: User = {
              ...cachedUserDataRaw,
              role: normalizedRole || UserRole.USER, // Default to USER if role is invalid
            };
            setUser(cachedUserData);
            
            // Load organization from localStorage if available
            const storedOrgData = localStorage.getItem(env.ORGANIZATION_STORAGE_KEY);
            if (storedOrgData) {
              try {
                const cachedOrgData: Organization = JSON.parse(storedOrgData);
                setOrganization(cachedOrgData);
              } catch (orgParseError) {
                console.error('Failed to parse stored organization data:', orgParseError);
              }
            }
            
            setIsLoading(false);
            
            // Then, validate token with backend and update with fresh data
            try {
              const freshUserDataRaw = await authService.validateToken(storedToken);
              if(!freshUserDataRaw.valid) {
                logout();
                return;
              }
              // Normalize role if needed
              const normalizedRole = normalizeRole(freshUserDataRaw.role);
              const freshUserData: User = {
                ...freshUserDataRaw,
                role: normalizedRole || UserRole.USER, // Default to USER if role is invalid
              };
              // Update state with fresh data
              setUser(freshUserData);
              // Update localStorage with fresh data
              localStorage.setItem(env.USER_STORAGE_KEY, JSON.stringify(freshUserData));
              
              // Update organization if present in user data or separate storage
              if (freshUserData.organization) {
                setOrganization(freshUserData.organization);
                localStorage.setItem(env.ORGANIZATION_STORAGE_KEY, JSON.stringify(freshUserData.organization));
              }
            } catch (validationError) {
              console.error('Token validation failed:', validationError);
              // Clear invalid token and data
              await authService.logout();
              setUser(null);
              setOrganization(null);
            }
          } catch (parseError) {
            console.error('Failed to parse stored user data:', parseError);
            // Clear invalid data
            logout();
            setIsLoading(false);
          }
        } else {
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Failed to initialize auth:', error);
        // Clear invalid token
        authService.logout();
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Login user with auth response and token
   * Called after successful OTP validation
   */
  const login = useCallback((authResponse: AuthResponse, token: string) => {
    // Store token
    authService.setToken(token);
    localStorage.setItem(env.JWT_STORAGE_KEY, token);

    // Normalize role if needed
    const normalizedRole = normalizeRole(authResponse.user?.roles?.[0] || authResponse.role);
    
    // Create username from first_name and last_name combination
    const firstName = authResponse.user?.first_name || '';
    const lastName = authResponse.user?.last_name || '';
    const fullName = `${firstName} ${lastName}`.trim();
    
    // Store organization if present in auth response
    if (authResponse.organization) {
      setOrganization(authResponse.organization);
      localStorage.setItem(env.ORGANIZATION_STORAGE_KEY, JSON.stringify(authResponse.organization));
    }
    
    // Create user object from auth response
    const userData: User = {
      userId: authResponse.user?.id,
      username: fullName || authResponse.user?.email || authResponse.email || authResponse.username,
      firstName: authResponse.user?.first_name,
      lastName: authResponse.user?.last_name,
      role: normalizedRole || UserRole.USER, // Default to USER if role is invalid
      email: authResponse.user?.email || authResponse.email,
      organization_id: authResponse.tenant?.id,
      tenant_id: authResponse.tenant?.id,
      isLoggedIn: true,
      organization: authResponse.organization,
    };

    // Store user data
    localStorage.setItem(env.USER_STORAGE_KEY, JSON.stringify(userData));
    setUser(userData);
  }, [authService]);

  /**
   * Logout user and clear stored data
   * Also broadcasts logout event to other tabs
   */
  const logout = useCallback(async () => {
    try {
      // Broadcast logout event to other tabs before clearing data
      if (typeof window !== 'undefined') {
        const logoutEvent = {
          timestamp: Date.now(),
          type: 'logout',
        };
        localStorage.setItem(env.LOGOUT_EVENT_KEY, JSON.stringify(logoutEvent));
        // Remove the event after a short delay to allow other tabs to detect it
        setTimeout(() => {
          localStorage.removeItem(env.LOGOUT_EVENT_KEY);
        }, 100);
      }
      
      setUser(null);
      setOrganization(null);
      if (typeof window !== 'undefined') {
        localStorage.removeItem(env.ORGANIZATION_STORAGE_KEY);
      }
      router.push('/login');
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  }, [authService, router]);

  /**
   * Validate current token
   */
  const validateToken = useCallback(async (): Promise<boolean> => {
    try {
      const storedToken = authService.getStoredToken();
      
      if (!storedToken) {
        return false;
      }

      const userData = await authService.validateToken(storedToken);
      setUser(userData);
      
      // Update organization if present in user data
      if (userData.organization) {
        setOrganization(userData.organization);
        localStorage.setItem(env.ORGANIZATION_STORAGE_KEY, JSON.stringify(userData.organization));
      }
      
      return true;
    } catch (error) {
      console.error('Token validation error:', error);
      await authService.logout();
      setUser(null);
      setOrganization(null);
      return false;
    }
  }, [authService]);

  /**
   * Check if user has required role(s)
   */
  const hasRole = useCallback((roles: UserRole | UserRole[]): boolean => {
    if (!user) return false;

    // Ensure user role is normalized
    const userRole = normalizeRole(user.role) || user.role;
    const roleArray = Array.isArray(roles) ? roles : [roles];
    return roleArray.includes(userRole);
  }, [user]);

  /**
   * Check if user has access to a feature
   * Returns true if feature is enabled in organization, or if no features array exists (default behavior)
   */
  const hasFeature = useCallback((featureId: string): boolean => {
    // If no organization, default to monitors and triage only
    if (!organization) {
      return DEFAULT_FEATURES.includes(featureId as any);
    }
    
    // If no features array, default to monitors and triage only
    if (!organization.features || organization.features.length === 0) {
      return DEFAULT_FEATURES.includes(featureId as any);
    }
    
    // Check if feature exists and is enabled
    const feature = organization.features.find(f => f.id === featureId);
    return feature?.enabled === true;
  }, [organization]);

  /**
   * Update current user data
   * Used when user edits their own profile
   */
  const updateUser = useCallback((data: UpdateUserData) => {
    if (!user) return;

    // Update user state with new data
    const updatedUser: User = {
      ...user,
      ...(data.firstName !== undefined && { firstName: data.firstName }),
      ...(data.lastName !== undefined && { lastName: data.lastName }),
      ...(data.email !== undefined && { email: data.email }),
      // Update username if firstName or lastName changed
      username: data.firstName || data.lastName
        ? `${data.firstName || user.firstName || ''} ${data.lastName || user.lastName || ''}`.trim() || user.username
        : user.username,
    };

    setUser(updatedUser);
    
    // Update localStorage
    if (typeof window !== 'undefined') {
      localStorage.setItem(env.USER_STORAGE_KEY, JSON.stringify(updatedUser));
    }
  }, [user]);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    validateToken,
    hasRole,
    organization,
    hasFeature,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * useAuth Hook
 * Access authentication context
 */
export function useAuth() {
  const context = useContext(AuthContext);
  
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
}
